import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CandidateState, ElectionStatus } from '../../_generatedTypes.g';
import { FilterList } from '../ui/FilterList';
import { useDispatch } from 'react-redux';
import { canVote } from '../../Election';
import { getTranslator } from '../../Translator';
import { ElectionContainer } from '../election/ElectionContainer';
import { DocumentTitle } from '../DocumentTitle';
import { Link } from 'react-router-dom';
import ElectionDetails from '../election/ElectionDetails';
import { QuestionMark } from '../ui/QuestionMark';
import { canEditElection, isCandidateAlready } from '../../selectors';
import { doesCandidateMatchText } from '../party/PartyComponent';
import { Toggle } from '../ui/Toggle';
import { addBlankCandidateSystemCandidateAndSetEditMode, addNewCandidateSystemCandidateForCandidacy, removeInlineCandidateIfEmpty } from '../../features/candidate/candidatesSlice';
import { CandidateItem } from '../candidate/CandidateItem';
import { csCandidateSearched } from '../../features/search/searchSlice';
import { addModeSet } from '../../features/party/PartyScreenSlice';
import { useAppSelector } from '../../hooks';

type ElectionCandidatesScreenParams = {
    electionId: string;
}

export const ElectionCandidatesScreen = () => {
    const partyScreenState = useAppSelector(state => state.partyScreen);

    const candidates = useAppSelector(state => state.candidates);
    const elections = useAppSelector(state => state.elections);
    const language = useAppSelector(state => state.language);
    const searchTermCSCandidate = useAppSelector(state => state.searchItemState.SearchTermCSCandidate);

    const dispatch = useDispatch();
    const routeElectionId = Number(useParams<ElectionCandidatesScreenParams>().electionId);
    const navigate = useNavigate();


    const election = elections.filter(election => election.ElectionId === routeElectionId).shift()!
    const t = getTranslator(language);
    const account = useAppSelector(state => state.clientState.Account);
    const candidatesFiltered = candidates.filter(c => c.ElectionId === routeElectionId);

    const addMode = partyScreenState.addMode;
    const canEdit = canEditElection(account, elections, routeElectionId);

    const canShowCandidacyApproved = account.IsAdmin;


    const onkeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            removeInlineCandidateIfEmpty(partyScreenState.inlineEditingCandidateId, candidates, dispatch);
            dispatch(addModeSet({ addMode: false }));
        }
    };

    return <ElectionContainer electionId={election.ElectionId}>
        <div onKeyDown={onkeyDown} key={election.ElectionId} id={`Election-${election.ElectionId}`}>
            <DocumentTitle title={t.translate(election.Name)} />
            <ElectionDetails election={election} t={t} isAdmin={account.IsAdmin} />
            {election.ElectionStatus == ElectionStatus.CandidacyPeriod && !isCandidateAlready(candidates, election.ElectionId) &&
                <div className="election__CandidacyExplanation">
                    <h4>
                        {t.translate({ NL: 'Kandidaatstelling uitleg:', EN: 'Candidacy explanation.' })}
                    </h4>
                    <ul>
                        <li>
                            {t.translate({
                                NL: 'Om jezelf als kandidaat toe te voegen klik op de knop "Ik wil me kandidaat stellen".',
                                EN: 'To add yourself as a candidate, click on the "I want to be a candidate" button.'
                            })}
                        </li>
                    </ul>
                </div>
            }
            {!candidatesFiltered.length
                ? <p>{t['No candidates have been registered for this election yet.']}</p>
                : <div>
                    <QuestionMark>{t['To find the candidate you want to vote for, enter a part of his/her name, slogan or promotional text.']}</QuestionMark>
                    <h2>{t['Candidates']}</h2>
                    <FilterList<CandidateState>
                        searchTerm={searchTermCSCandidate}
                        onChange={v => dispatch(csCandidateSearched(v))}
                        label={t['Search candidate']}
                        className="party__candidates"
                        items={candidatesFiltered}
                        language={t.languageCode}
                        filter={(candidate, searchTerm) => doesCandidateMatchText(candidate, searchTerm, t)}
                        render={(candidate) => <CandidateItem candidateId={candidate.ClientSideId} key={candidate.ClientSideId} candidateSystem={true} canShowCandidacyApproved={canShowCandidacyApproved} />} />
                </div>}
            <div className="buttonrow">
                {canVote(election)
                    ? <Link className="linkbutton" id={`Election-${election.ElectionId}-BlankVote`} to={`/blankvote/${election.ElectionId}`}>{t['Blank vote']}</Link>
                    : <span className="linkbutton linkbutton--disabled" id={`Election-${election.ElectionId}-BlankVote`}>{t['Blank vote']}</span>}
                <Link className="linkbutton" to="/">{t['Back']}</Link>
                {account.IsAdmin && canEdit && <Toggle state={addMode} onChange={(on) => {
                    if (on) {
                        addBlankCandidateSystemCandidateAndSetEditMode(election.ElectionId, dispatch);
                    }
                    else {
                        removeInlineCandidateIfEmpty(partyScreenState.inlineEditingCandidateId, candidates, dispatch);
                    }
                    dispatch(addModeSet({ addMode: on }));
                }}>{addMode ? t['Done adding candidates'] : t['Add candidates']}</Toggle>}
                {(!account.IsAdmin && election.ElectionStatus === ElectionStatus.CandidacyPeriod && !isCandidateAlready(candidates, election.ElectionId)) && <button className="home__addbutton" onClick={() => { addNewCandidateSystemCandidateForCandidacy(election.ElectionId, account.UserName, dispatch); navigate('/candidateEdit/0') }}>{t['I want to be a candidate']}</button>}
            </div>
        </div>
    </ElectionContainer>;
}
